import { useSettings } from '../context/SettingsContext';
import { convertToTimezone, formatDate, formatDateOnly } from '../utils/dateUtils';

/**
 * Hook to easily access and use the user's timezone for date formatting.
 * 
 * @returns An object with timezone utilities
 */
export function useUserTimezone() {
  const { settings } = useSettings();
  const timezone = settings.timezone || 'UTC';
  
  /**
   * Format a date using the user's timezone
   */
  const formatWithTimezone = (date: Date | string): string => {
    return formatDate(date, timezone);
  };
  
  /**
   * Format a date-only value using the user's timezone
   */
  const formatDateOnlyWithTimezone = (date: Date | string): string => {
    return formatDateOnly(date, timezone);
  };
  
  /**
   * Convert a date to the user's timezone
   */
  const toUserTimezone = (date: Date | string): Date => {
    return convertToTimezone(date, timezone);
  };
  
  /**
   * Get the current time in the user's timezone
   */
  const getCurrentTimeInUserTimezone = (): Date => {
    return convertToTimezone(new Date(), timezone);
  };
  
  /**
   * Check if a date is today in the user's timezone
   */
  const isToday = (date: Date | string): boolean => {
    const dateInUserTz = toUserTimezone(date);
    const todayInUserTz = getCurrentTimeInUserTimezone();
    
    return (
      dateInUserTz.getFullYear() === todayInUserTz.getFullYear() &&
      dateInUserTz.getMonth() === todayInUserTz.getMonth() &&
      dateInUserTz.getDate() === todayInUserTz.getDate()
    );
  };
  
  return {
    timezone,
    formatWithTimezone,
    formatDateOnlyWithTimezone,
    toUserTimezone,
    getCurrentTimeInUserTimezone,
    isToday
  };
}

// Only using default export to maintain backward compatibility
export default useUserTimezone; 